/** Settings for one title: release slots, scheduled writing, my usual settings, and who works here. */
import { useState } from 'react'
import { switchCanFollow, type SwitchName } from './settingsSwitch'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addPhotoSite, addStaffEmail, deleteScheduledBrief, getStaffEmails, removeStaffEmail, replaceStaffEmail, staffEmailsPath, type StaffEmails, getPeople, listBrands, listPhotoSites, removePhotoSite, type PhotoSite, mergeBrand, renameBrand, type BrandRow, getTitleSettings, getSearchBlocks, removeSearchBlock, putEditorialDefaults, putPublishTarget, putScheduledBrief, putSlots, putSwitches,
  type PublishTarget, type SwitchesBody, type TitleSettings,
} from './api'
import { publishTargetLabel } from './labels'
import { DAY_SHORT, legacyToEt, slotsText, toggleDay } from './schedule'
import { useEditorialTitle } from './titleContext'
import { DraftBriefForm } from '../pages/DraftBriefForm'
import { describeBrief, FORMAT_OPTIONS, SOURCE_OPTIONS, supportsMembership, type Brief } from '../lib/brief'
import { describeError } from '../lib/describeError'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const card = 'flex flex-col gap-3.5 rounded-xl border border-ink-500 bg-ink-700 p-[22px]'
const field = `box-border min-h-11 rounded-lg border border-ink-400 bg-ink-800 px-3 py-2.5 font-ui text-[14px] text-paper disabled:opacity-60 ${focus}`
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`
const link = `font-ui text-[14px] text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`
const pill = 'inline-flex h-6 items-center rounded-full bg-ink-600 px-2.5 font-ui text-[12px] font-semibold text-paper-dim'

const ADMIN_ONLY = <span className={pill}>Administrators only</span>

const SWITCH_HEADING: Record<SwitchName, string> = { releases: 'Releases', 'scheduled writing': 'Scheduled writing', versions: 'Make a version', import: 'Import', 'photo help': 'Ask about photos', 'weekly package': 'Weekly package', 'weekly live': 'Send to real readers (off = staff only)', 'wp auto': 'Publish from WordPress automatically', 'search ideas': 'Search ideas (DataForSEO)', 'improve this page': 'Improve this page' }

function SwitchRow({ title, label, on, source, canAdmin, busy, onChange }: {
  title: string; label: SwitchName; on: boolean; source: string; canAdmin: boolean; busy: boolean
  onChange: (v: boolean | null) => void
}) {
  // Versions and Import have no server setting to follow: off means off.
  const canFollow = switchCanFollow(label)
  const follows = canFollow && source === 'server'
  const state = follows ? 'follow' : on ? 'on' : 'off'
  const opts: { key: string; text: string; value: boolean | null }[] = [
    { key: 'on', text: 'On', value: true },
    { key: 'off', text: 'Off', value: false },
    ...(canFollow ? [{ key: 'follow', text: `Follow the server setting (currently ${on ? 'On' : 'Off'})`, value: null }] : []),
  ]
  const pick = (value: boolean | null) => {
    const msg = value === false
      ? (label === 'releases'
        ? `Turn releases off for ${title}? Scheduled pieces wait until you turn them back on.`
        : label === 'versions' ? `Turn Make a version off for ${title}?` : label === 'import' ? `Turn Import off for ${title}?` : label === 'photo help' ? `Turn Ask about photos off for ${title}?` : label === 'improve this page' ? `Turn Improve this page off for ${title}? The Ideas section and its Refresh this page buttons go away; refreshed pages already saved keep their results.` : label === 'search ideas' ? `Turn Search ideas off for ${title}? Search-demand ideas stop being gathered.` : label === 'weekly package' ? `Turn the weekly package off for ${title}? Nothing will be planned or sent.` : label === 'weekly live' ? `Send the weekly package to staff only for ${title}? Real readers stop getting it.` : label === 'wp auto' ? `Stop publishing from WordPress automatically for ${title}? New WordPress posts will wait on the From WordPress list for an editor.` : `Turn scheduled writing off for ${title}?`)
      : value === true ? (label === 'wp auto' ? `Publish from WordPress automatically for ${title}? Every post published in WordPress (free and members') will be imported and go live within about 15 minutes, with no one reviewing it. Imported posts still waiting go live too.` : `Turn ${label} on for ${title}?`) : `Make ${label} follow the server setting for ${title}?`
    if (!window.confirm(msg)) return
    onChange(value)
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-ui text-[14px] font-semibold text-paper">{SWITCH_HEADING[label]}</span>
        {!canAdmin && ADMIN_ONLY}
      </div>
      <div role="radiogroup" aria-label={SWITCH_HEADING[label]} className="flex flex-wrap gap-2">
        {opts.map((o) => (
          <button key={o.key} type="button" role="radio" aria-checked={state === o.key} disabled={!canAdmin || busy}
            onClick={() => state !== o.key && pick(o.value)}
            className={`min-h-10 rounded-lg border px-3.5 font-ui text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${focus} ${state === o.key ? 'border-paper bg-paper text-ink-900' : 'border-ink-400 text-paper hover:bg-ink-600'}`}>
            {o.text}
          </button>
        ))}
      </div>
    </div>
  )
}

function SearchBlocks({ pub }: { pub: string }) {
  const qc = useQueryClient()
  const key = ['editorial', 'search-blocks', pub]
  const q = useQuery({ queryKey: key, queryFn: () => getSearchBlocks(pub) })
  const rm = useMutation({ mutationFn: (kw: string) => removeSearchBlock(pub, kw), onSuccess: () => { void qc.invalidateQueries({ queryKey: key }) } })
  const items = q.data?.items ?? []
  return (
    <div className="flex flex-col gap-2">
      <span className="font-ui text-[14px] font-semibold text-paper">Blocked search phrases</span>
      {q.isError && <p role="alert" className="m-0 text-[13px] text-radar-red">{describeError(q.error)}</p>}
      {rm.isError && <p role="alert" className="m-0 text-[13px] text-radar-red">{describeError(rm.error)}</p>}
      {q.isSuccess && !items.length && <p className="m-0 text-[13px] text-paper-faint">None blocked.</p>}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {items.map((b) => (
          <li key={b.keyword} className="flex flex-wrap items-center gap-3 text-[14px]">
            <span className="text-paper">{b.keyword}</span>
            <button type="button" className={btn} disabled={rm.isPending} onClick={() => rm.mutate(b.keyword)}>Remove</button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function BrandsSection({ pub, titleLabel }: { pub: string; titleLabel: string }) {
  const qc = useQueryClient()
  const key = ['editorial', 'brands', pub]
  const q = useQuery({ queryKey: key, queryFn: () => listBrands(pub) })
  const [rename, setRename] = useState<{ id: number; name: string } | null>(null)
  const [merge, setMerge] = useState<{ id: number; into: number | null; confirm: boolean } | null>(null)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const settle = (text: string) => {
    setRename(null); setMerge(null); setMsg({ ok: true, text })
    void qc.invalidateQueries({ queryKey: key })
  }
  const fail = (e: unknown) => setMsg({ ok: false, text: describeError(e) })
  const renameM = useMutation({ mutationFn: (v: { id: number; name: string }) => renameBrand(v.id, v.name), onSuccess: (b) => settle(`Renamed to ${b.name}.`), onError: fail })
  const mergeM = useMutation({ mutationFn: (v: { id: number; into: number }) => mergeBrand(v.id, v.into), onSuccess: (b) => settle(`Merged into ${b.name}.`), onError: fail })
  const brands: BrandRow[] = q.data ?? []
  const busy = renameM.isPending || mergeM.isPending
  return (
    <section aria-labelledby="set-brands" className={card}>
      <h2 id="set-brands" className="m-0 font-serif text-[22px] font-normal text-paper">Brands</h2>
      <p className="m-0 text-[14px] text-paper-dim">
        Tidy the brands {titleLabel} files pieces under. A renamed or merged brand’s old address goes to the new one.
      </p>
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`m-0 text-[13px] ${msg.ok ? 'text-radar-green' : 'text-radar-red'}`}>{msg.text}</p>}
      {q.isLoading && <p className="m-0 text-[14px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 text-[13px] text-radar-red">{describeError(q.error)}</p>}
      {q.data && brands.length === 0 && <p className="m-0 text-[14px] text-paper-dim">No brands yet.</p>}
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {brands.map((b) => {
          const renaming = rename?.id === b.id
          const merging = merge?.id === b.id
          const target = merging ? brands.find((x) => x.id === merge.into) : undefined
          return (
            <li key={b.id} className="flex flex-col gap-2 border-t border-ink-500 pt-3 first:border-t-0 first:pt-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px]">
                <span className="font-semibold text-paper">{b.name}</span>
                <span className={pill}>{b.live_count} live</span>
                <span className="text-paper-faint">/{b.slug}</span>
                {!renaming && !merging && (
                  <span className="ml-auto flex gap-2">
                    <button type="button" className={btn} disabled={busy} onClick={() => { setMsg(null); setMerge(null); setRename({ id: b.id, name: b.name }) }}>Rename</button>
                    <button type="button" className={btn} disabled={busy || brands.length < 2} onClick={() => { setMsg(null); setRename(null); setMerge({ id: b.id, into: null, confirm: false }) }}>Merge into…</button>
                  </span>
                )}
              </div>
              {renaming && (
                <form className="flex flex-wrap items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); if (rename.name.trim()) renameM.mutate({ id: b.id, name: rename.name.trim() }) }}>
                  <label className="sr-only" htmlFor={`brand-name-${b.id}`}>New name for {b.name}</label>
                  <input id={`brand-name-${b.id}`} className={field} value={rename.name} maxLength={120}
                    onChange={(e) => setRename({ id: b.id, name: e.target.value })} />
                  <button type="submit" className={btnPrimary} disabled={busy || !rename.name.trim() || rename.name.trim() === b.name}>{renameM.isPending ? 'Saving…' : 'Save name'}</button>
                  <button type="button" className={btn} disabled={busy} onClick={() => setRename(null)}>Cancel</button>
                </form>
              )}
              {merging && !merge.confirm && (
                <div className="flex flex-wrap items-center gap-2">
                  <label className="sr-only" htmlFor={`brand-into-${b.id}`}>Merge {b.name} into</label>
                  <select id={`brand-into-${b.id}`} className={field} value={merge.into ?? ''}
                    onChange={(e) => setMerge({ id: b.id, into: e.target.value ? Number(e.target.value) : null, confirm: false })}>
                    <option value="">Pick a brand…</option>
                    {brands.filter((x) => x.id !== b.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                  <button type="button" className={btnPrimary} disabled={merge.into === null} onClick={() => setMerge({ ...merge, confirm: true })}>Continue</button>
                  <button type="button" className={btn} onClick={() => setMerge(null)}>Cancel</button>
                </div>
              )}
              {merging && merge.confirm && target && (
                <div role="group" aria-label={`Confirm merging ${b.name}`} className="flex flex-col gap-2 rounded-lg border border-ink-400 bg-ink-800 p-3">
                  <p className="m-0 text-[14px] text-paper">
                    Merge {b.name} into {target.name}? Its {b.live_count} live {b.live_count === 1 ? 'piece moves' : 'pieces move'} to {target.name} and {b.name} goes. This can’t be undone.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={btnPrimary} disabled={busy} onClick={() => mergeM.mutate({ id: b.id, into: target.id })}>{mergeM.isPending ? 'Merging…' : `Merge into ${target.name}`}</button>
                    <button type="button" className={btn} disabled={busy} onClick={() => setMerge({ ...merge, confirm: false })}>Back</button>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function PhotoSitesSection({ canAdmin }: { canAdmin: boolean }) {
  const qc = useQueryClient()
  const key = ['editorial', 'photo-sites']
  const q = useQuery({ queryKey: key, queryFn: listPhotoSites })
  const [domain, setDomain] = useState('')
  const [brand, setBrand] = useState('')
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const fail = (e: unknown) => setMsg({ ok: false, text: describeError(e) })
  const addM = useMutation({
    mutationFn: () => addPhotoSite(domain.trim(), brand.trim()),
    onSuccess: (s) => { setDomain(''); setBrand(''); setMsg({ ok: true, text: `Added ${s.domain} (${s.brand}).` }); void qc.invalidateQueries({ queryKey: key }) },
    onError: fail,
  })
  const removeM = useMutation({
    mutationFn: (s: PhotoSite) => removePhotoSite(s.id).then(() => s),
    onSuccess: (s) => { setMsg({ ok: true, text: `Removed ${s.domain}.` }); void qc.invalidateQueries({ queryKey: key }) },
    onError: fail,
  })
  const sites: PhotoSite[] = q.data ?? []
  const busy = addM.isPending || removeM.isPending
  return (
    <section aria-labelledby="set-photo-sites" className={card}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="set-photo-sites" className="m-0 font-serif text-[22px] font-normal text-paper">Manufacturer photo sites</h2>
        {!canAdmin && ADMIN_ONLY}
      </div>
      <p className="m-0 text-[14px] text-paper-dim">
        When a piece cites a page on one of these sites, the writer copies that page’s product photos and credits them to the brand.
        One is used as the main photo only when no archive photo shows the subject; the rest are offered with the piece’s photos.
        Photos from any other site (shops, news sites) are never used. This list is shared by all four titles.
      </p>
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`m-0 text-[13px] ${msg.ok ? 'text-radar-green' : 'text-radar-red'}`}>{msg.text}</p>}
      {q.isLoading && <p className="m-0 text-[14px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 text-[13px] text-radar-red">{describeError(q.error)}</p>}
      {q.data && sites.length === 0 && <p className="m-0 text-[14px] text-paper-dim">No sites yet, so no manufacturer photos are used.</p>}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {sites.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-ink-500 pt-2 text-[14px] first:border-t-0 first:pt-0">
            <span className="font-semibold text-paper">{s.brand}</span>
            <span className="text-paper-faint">{s.domain}</span>
            {canAdmin && (
              <button type="button" className={`${btn} ml-auto`} disabled={busy}
                onClick={() => { if (window.confirm(`Stop using photos from ${s.domain}? Photos already in pieces stay.`)) { setMsg(null); removeM.mutate(s) } }}>
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
      {canAdmin && (
        <form className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); if (domain.trim() && brand.trim()) { setMsg(null); addM.mutate() } }}>
          <label className="flex flex-col gap-1 text-[13px] text-paper-dim">
            Website
            <input className={field} value={domain} placeholder="axialadventure.com" maxLength={300} onChange={(e) => setDomain(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-paper-dim">
            Brand (the photo credit)
            <input className={field} value={brand} placeholder="Axial" maxLength={120} onChange={(e) => setBrand(e.target.value)} />
          </label>
          <button type="submit" className={btnPrimary} disabled={busy || !domain.trim() || !brand.trim()}>{addM.isPending ? 'Adding…' : 'Add site'}</button>
        </form>
      )}
    </section>
  )
}

function StaffEmailsSection({ id, heading, intro, empty, path, canAdmin }: {
  id: string; heading: string; intro: string; empty: string; path: string; canAdmin: boolean
}) {
  const qc = useQueryClient()
  const key = ['editorial', 'staff-emails', path]
  const q = useQuery({ queryKey: key, queryFn: () => getStaffEmails(path) })
  const [adding, setAdding] = useState('')
  const [editing, setEditing] = useState<{ old: string; value: string } | null>(null)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const fail = (e: unknown) => setMsg({ ok: false, text: describeError(e) })
  const saved = (data: StaffEmails, text: string) => { qc.setQueryData(key, data); setMsg({ ok: true, text }) }
  const addM = useMutation({
    mutationFn: (email: string) => addStaffEmail(path, email),
    onSuccess: (d, email) => { setAdding(''); saved(d, `Added ${email.trim().toLowerCase()}.`) },
    onError: fail,
  })
  const editM = useMutation({
    mutationFn: (v: { old: string; value: string }) => replaceStaffEmail(path, v.old, v.value),
    onSuccess: (d, v) => { setEditing(null); saved(d, `Changed ${v.old} to ${v.value.trim().toLowerCase()}.`) },
    onError: fail,
  })
  const removeM = useMutation({
    mutationFn: (email: string) => removeStaffEmail(path, email),
    onSuccess: (d, email) => saved(d, `Removed ${email}.`),
    onError: fail,
  })
  const busy = addM.isPending || editM.isPending || removeM.isPending
  const emails = q.data?.emails ?? []
  return (
    <section aria-labelledby={id} className={card}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 id={id} className="m-0 font-serif text-[22px] font-normal text-paper">{heading}</h2>
        {!canAdmin && ADMIN_ONLY}
      </div>
      <p className="m-0 text-[14px] text-paper-dim">{intro}</p>
      {q.data?.source === 'server' && (
        <p className="m-0 text-[13px] text-paper-dim">These addresses come from the server setting. Your first change here saves them on this page, then this list is the one used.</p>
      )}
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`m-0 text-[13px] ${msg.ok ? 'text-radar-green' : 'text-radar-red'}`}>{msg.text}</p>}
      {q.isLoading && <p className="m-0 text-[14px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 text-[13px] text-radar-red">{describeError(q.error)}</p>}
      {q.data && emails.length === 0 && <p className="m-0 text-[14px] text-paper-dim">{empty}</p>}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {emails.map((email) => (
          <li key={email} className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-ink-500 pt-2 text-[14px] first:border-t-0 first:pt-0">
            {editing?.old === email ? (
              <form className="flex flex-1 flex-wrap items-center gap-2"
                onSubmit={(e) => { e.preventDefault(); if (editing.value.trim()) { setMsg(null); editM.mutate(editing) } }}>
                <input className={`${field} min-w-0 flex-1`} type="email" aria-label={`New address for ${email}`} value={editing.value} maxLength={320} autoFocus
                  onChange={(e) => setEditing({ old: email, value: e.target.value })} />
                <button type="submit" className={btnPrimary} disabled={busy || !editing.value.trim()}>{editM.isPending ? 'Saving…' : 'Save'}</button>
                <button type="button" className={btn} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
              </form>
            ) : (
              <>
                <span className="min-w-0 break-all text-paper">{email}</span>
                {canAdmin && (
                  <span className="ml-auto flex gap-2">
                    <button type="button" className={btn} disabled={busy} onClick={() => { setMsg(null); setEditing({ old: email, value: email }) }}>Edit</button>
                    <button type="button" className={btn} disabled={busy}
                      onClick={() => { if (window.confirm(`Stop sending these emails to ${email}?`)) { setMsg(null); removeM.mutate(email) } }}>
                      Remove
                    </button>
                  </span>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {canAdmin && (
        <form className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); if (adding.trim()) { setMsg(null); addM.mutate(adding) } }}>
          <label className="flex flex-col gap-1 text-[13px] text-paper-dim">
            Email address
            <input className={field} type="email" value={adding} placeholder="name@airage.com" maxLength={320} onChange={(e) => setAdding(e.target.value)} />
          </label>
          <button type="submit" className={btnPrimary} disabled={busy || !adding.trim()}>{addM.isPending ? 'Adding…' : 'Add address'}</button>
        </form>
      )}
    </section>
  )
}

export function SettingsPage() {
  const { section, publicationSlug: slug } = useEditorialTitle()
  const qc = useQueryClient()
  const key = ['editorial', 'settings', slug]
  const q = useQuery({ queryKey: key, queryFn: () => getTitleSettings(slug) })
  const people = useQuery({ queryKey: ['editorial', 'people', slug], queryFn: () => getPeople(slug) })
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [editBrief, setEditBrief] = useState(false)
  const [draft, setDraft] = useState<{ days: number[]; time: string } | null>(null)
  const done = (msg: string) => { setError(null); setSaved(msg); void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ['editorial', 'schedule', slug] }) }
  const fail = (e: unknown) => { setSaved(null); setError(describeError(e)) }

  const slotsM = useMutation({
    mutationFn: (v: { days: number[]; time: string }) => putSlots(slug, { days_of_week: v.days, release_time: v.time }),
    onSuccess: () => { setDraft(null); done('Slots saved.') }, onError: fail,
  })
  const switchM = useMutation({ mutationFn: (b: SwitchesBody) => putSwitches(slug, b), onSuccess: () => { done('Saved.'); void qc.invalidateQueries({ queryKey: ['editorial-desk', slug] }) }, onError: fail })
  const targetM = useMutation({ mutationFn: (t: PublishTarget) => putPublishTarget(slug, t), onSuccess: () => done('Saved.'), onError: fail })
  const briefM = useMutation({
    mutationFn: (b: Brief) => putScheduledBrief(slug, b),
    onSuccess: () => { setEditBrief(false); done('Brief saved.') }, onError: fail,
  })
  const resetM = useMutation({ mutationFn: () => deleteScheduledBrief(slug), onSuccess: () => done('Back to the defaults.'), onError: fail })
  const defaultsM = useMutation({
    mutationFn: (b: Pick<Brief, 'format' | 'sources' | 'destination'>) => putEditorialDefaults(b),
    onSuccess: () => done('Saved.'), onError: fail,
  })

  if (q.isLoading) return <p className="font-ui text-paper-dim">Loading…</p>
  if (q.isError || !q.data) return <p role="alert" className="font-ui text-radar-red">{describeError(q.error)}</p>
  const s: TitleSettings = q.data
  const canAdmin = s.can_admin
  const et = s.slots ? legacyToEt(s.slots) : null
  const days = draft?.days ?? et?.days_of_week ?? []
  const time = draft?.time ?? et?.release_time ?? '07:30'
  const reason = days.length === 0 ? 'Pick at least one day' : null
  const defaults = s.my_defaults
  const saveDefault = (patch: Partial<Pick<Brief, 'format' | 'sources' | 'destination'>>) =>
    defaultsM.mutate({ format: defaults.format, sources: defaults.sources, destination: defaults.destination, ...patch })
  const members = supportsMembership(slug)

  return (
    <div className="flex max-w-3xl flex-col gap-6 font-ui">
      <header className="flex flex-col gap-1.5">
        <div className="label">{section.label} · Settings</div>
        <h1 className="m-0 font-serif text-[32px] font-normal leading-tight text-paper">Settings</h1>
      </header>
      {saved && <p role="status" className="m-0 text-[14px] text-radar-green">{saved}</p>}
      {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}

      <section aria-labelledby="set-slots" className={card}>
        <h2 id="set-slots" className="m-0 font-serif text-[22px] font-normal text-paper">Release slots</h2>
        <p className="m-0 text-[14px] text-paper-dim">Scheduled pieces go out one per slot, at this time.</p>
        {et?.converted && <p className="m-0 text-[13px] text-amber-glow">Saving moves these slots to Eastern time.</p>}
        <div className="flex flex-wrap gap-2">
          {DAY_SHORT.map((name, d) => (
            <button key={name} type="button" aria-pressed={days.includes(d)}
              onClick={() => setDraft({ days: toggleDay(days, d), time })}
              className={`inline-flex min-h-11 min-w-[52px] items-center justify-center rounded-lg border px-2 text-[14px] font-semibold ${focus} ${days.includes(d) ? 'border-paper bg-paper text-ink-900' : 'border-ink-400 text-paper hover:bg-ink-600'}`}>
              {name}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="set-time" className="text-[14px] font-semibold text-paper">At</label>
          <input id="set-time" type="time" value={time} className={field} onChange={(e) => setDraft({ days, time: e.target.value })} />
          <span className="text-[13px] text-paper-dim">
            Eastern time{s.slots ? ` (${s.slots.release_time_utc_today} UTC)` : ''}
          </span>
        </div>
        {s.slots && <p className="m-0 text-[13px] text-paper-faint">Now: {slotsText(et?.days_of_week ?? [], et?.release_time ?? '07:30')}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={btnPrimary} disabled={!!reason || !time || slotsM.isPending} onClick={() => slotsM.mutate({ days, time })}>
            {slotsM.isPending ? 'Saving…' : 'Save slots'}
          </button>
          {reason && <span className="text-[13px] text-paper-dim">{reason}</span>}
        </div>
      </section>

      <section aria-labelledby="set-sched" className={card}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 id="set-sched" className="m-0 font-serif text-[22px] font-normal text-paper">Pieces written on a schedule</h2>
          {!canAdmin && ADMIN_ONLY}
        </div>
        <p className="m-0 text-[14px] text-paper-dim">
          The agent can write a piece by itself on a schedule, from the top open idea. It lands in To review; nothing publishes on its own.
        </p>
        <SwitchRow title={section.label} label="scheduled writing" on={s.scheduled_writing_on} source={s.scheduled_writing_source}
          canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ scheduled_writing_on: v })} />
        <div className="flex flex-col gap-2 border-t border-ink-500 pt-3.5">
          <span className="label">Brief for scheduled pieces</span>
          <p className="m-0 text-[14px] text-paper">{describeBrief(s.scheduled_brief.brief, section.label)}</p>
          {canAdmin && !editBrief && (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={btn} disabled={resetM.isPending}
                onClick={() => { if (window.confirm('Reset the scheduled brief to the defaults?')) resetM.mutate() }}>Reset to the defaults</button>
              <button type="button" className={btn} onClick={() => setEditBrief(true)}>Change</button>
            </div>
          )}
          {canAdmin && editBrief && (
            <div className="rounded-lg border border-ink-400 bg-ink-800 p-4">
              <DraftBriefForm scheduleMode initial={s.scheduled_brief.brief} title="Scheduled pieces" saveDefaults={false} submitLabel="Save brief"
                publicationSlug={slug} sectionLabel={section.label} busy={briefM.isPending}
                onSubmit={(b) => briefM.mutate(b)} onCancel={() => setEditBrief(false)} />
            </div>
          )}
        </div>
      </section>

      <section aria-labelledby="set-mine" className={card}>
        <h2 id="set-mine" className="m-0 font-serif text-[22px] font-normal text-paper">My usual settings</h2>
        <p className="m-0 text-[14px] text-paper-dim">What Start a piece fills in for you, on every title you work on.</p>
        <div className="flex flex-wrap gap-4">
          <label className="flex flex-col gap-1 text-[13px] font-semibold text-paper-dim">Length
            <select className={field} value={defaults.format} disabled={defaultsM.isPending}
              onChange={(e) => saveDefault({ format: e.target.value as Brief['format'] })}>
              {FORMAT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[13px] font-semibold text-paper-dim">Sources
            <select className={field} value={defaults.sources} disabled={defaultsM.isPending}
              onChange={(e) => saveDefault({ sources: e.target.value as Brief['sources'] })}>
              {SOURCE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          {members && (
            <label className="flex flex-col gap-1 text-[13px] font-semibold text-paper-dim">Readers
              <select className={field} value={defaults.destination} disabled={defaultsM.isPending}
                onChange={(e) => saveDefault({ destination: e.target.value as Brief['destination'] })}>
                <option value="section">{section.label}</option>
                <option value="membership">Members only</option>
                <option value="both">Both</option>
              </select>
            </label>
          )}
        </div>
        {!members && <p className="m-0 text-[13px] text-paper-faint">Titles without a members’ section always use their free section.</p>}
      </section>

      <section aria-labelledby="set-people" className={card}>
        <h2 id="set-people" className="m-0 font-serif text-[22px] font-normal text-paper">Who works on {section.label}</h2>
        {people.isError && <p role="alert" className="m-0 text-[13px] text-radar-red">{describeError(people.error)}</p>}
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {(people.data ?? []).map((p) => (
            <li key={p.email ?? p.name} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px]">
              <span className="font-semibold text-paper">{p.name}</span>
              <span className={pill}>{p.role}</span>
              <span className="text-paper-dim">{p.titles.join(', ')}</span>
              {p.email && <span className="text-paper-faint">{p.email}</span>}
            </li>
          ))}
        </ul>
        {canAdmin && <Link to="/users" className={link}>Manage people in the AAMCirc console</Link>}
      </section>

      <section aria-labelledby="set-switches" className={card}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 id="set-switches" className="m-0 font-serif text-[22px] font-normal text-paper">Switches</h2>
          {!canAdmin && ADMIN_ONLY}
        </div>
        <SwitchRow title={section.label} label="releases" on={s.releases_on} source={s.releases_source}
          canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ releases_on: v })} />
        <div className="border-t border-ink-500 pt-3.5">
          <SwitchRow title={section.label} label="versions" on={s.versions_on} source="title"
            canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ versions_on: v })} />
        </div>
        <div className="border-t border-ink-500 pt-3.5">
          <SwitchRow title={section.label} label="import" on={s.imports_on === true} source="title"
            canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ imports_on: v })} />
        </div>
        <div className="border-t border-ink-500 pt-3.5">
          <SwitchRow title={section.label} label="photo help" on={s.photo_help_on === true} source="title"
            canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ photo_help_on: v })} />
        </div>
        <div className="flex flex-col gap-2 border-t border-ink-500 pt-3.5">
          <SwitchRow title={section.label} label="search ideas" on={s.search_ideas_on === true} source="title"
            canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ search_ideas_on: v })} />
          {canAdmin && <SearchBlocks pub={slug} />}
        </div>
        <div className="border-t border-ink-500 pt-3.5">
          <SwitchRow title={section.label} label="improve this page" on={s.page_refresh_on === true} source="title"
            canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ page_refresh_on: v })} />
        </div>
        <div className="flex flex-col gap-1.5 border-t border-ink-500 pt-3.5">
          <SwitchRow title={section.label} label="wp auto" on={s.wp_auto_publish_on === true} source="title"
            canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ wp_auto_publish_on: v })} />
          <p className="m-0 max-w-2xl font-ui text-[13px] text-paper-dim">
            On: posts published in WordPress, free and members', are checked every 15 minutes, imported, approved and put live with no one pressing anything.
            Edits made in WordPress come over too, except to a piece edited in the desk, which waits on the From WordPress list. Off: nothing comes over until an editor imports it.
          </p>
        </div>
        {canAdmin && (
          <>
            <div className="border-t border-ink-500 pt-3.5">
              <SwitchRow title={section.label} label="weekly package" on={s.weekly_package_on === true} source="title"
                canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ weekly_package_on: v })} />
            </div>
            <div className="border-t border-ink-500 pt-3.5">
              <SwitchRow title={section.label} label="weekly live" on={s.weekly_package_live === true} source="title"
                canAdmin={canAdmin} busy={switchM.isPending} onChange={(v) => switchM.mutate({ weekly_package_live: v })} />
            </div>
          </>
        )}
      </section>

      <StaffEmailsSection id="set-heads-up" heading="Heads Up emails" path={staffEmailsPath('weekly-package')} canAdmin={canAdmin}
        intro="Who gets the Heads Up copies of each weekly package's emails, the staff-only sends, and an alert when a week is held. This list is shared by all four titles."
        empty="No one is on the list, so no Heads Up emails or hold alerts go out." />

      {section.readers_rides === true && (
        <StaffEmailsSection id="set-rides-emails" heading="Readers Rides notifications" path={staffEmailsPath({ readersRides: slug })} canAdmin={canAdmin}
          intro="Who gets an email when a new Readers Rides entry arrives, when a Build Challenge closes with no winner picked, and early in the month when last month's Ride of the Month isn't picked yet."
          empty="No one is on the list, so no Readers Rides notifications go out." />
      )}

      {s.section_brands_on === true && <BrandsSection pub={slug} titleLabel={section.label} />}

      <PhotoSitesSection canAdmin={canAdmin} />

      <section aria-labelledby="set-target" className={card}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 id="set-target" className="m-0 font-serif text-[22px] font-normal text-paper">Where new pieces publish</h2>
          {!canAdmin && ADMIN_ONLY}
        </div>
        <p className="m-0 text-[14px] text-paper-dim">Pieces already live stay where they are.</p>
        <div role="radiogroup" aria-label="Where new pieces publish" className="flex flex-wrap gap-2">
          {(['wordpress', 'native'] as const).map((t) => {
            const current = (s.publish_target ?? 'wordpress') === t
            return (
              <button key={t} type="button" role="radio" aria-checked={current} disabled={!canAdmin || targetM.isPending}
                onClick={() => {
                  if (current || !window.confirm(`New pieces for ${section.label} will publish to ${publishTargetLabel(t)}. Pieces already live stay where they are.`)) return
                  targetM.mutate(t)
                }}
                className={`min-h-10 rounded-lg border px-3.5 font-ui text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${focus} ${current ? 'border-paper bg-paper text-ink-900' : 'border-ink-400 text-paper hover:bg-ink-600'}`}>
                {publishTargetLabel(t)}
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
